import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { IDLE_LOGOUT_FLAG } from "@/contexts/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, User, Loader2, Zap, Building2, Wrench } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api } from "@/lib/api";

// Themed Unsplash stock visuals — electrical infra · civil structures · facility services
const BG_IMAGES = {
  electrical: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=900&q=80",
  civil:      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=900&q=80",
  facility:   "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=900&q=80",
};

export default function Login() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(null);
  const [idleNotice, setIdleNotice] = useState(false);
  const [pwChangedNotice, setPwChangedNotice] = useState(false);

  useEffect(() => {
    api.get("/public/brand").then(r => setBrand(r.data)).catch(() => {});
    try {
      if (sessionStorage.getItem(IDLE_LOGOUT_FLAG) === "1") {
        sessionStorage.removeItem(IDLE_LOGOUT_FLAG);
        setIdleNotice(true);
      }
      if (sessionStorage.getItem("radiants_pw_changed") === "1") {
        sessionStorage.removeItem("radiants_pw_changed");
        setPwChangedNotice(true);
      }
    } catch {}
  }, []);

  if (user) return <Navigate to="/" replace />;

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result) {
      toast.success("Welcome to Radiants PrimeWork");
      // Restricted personas land directly on their permitted page rather than
      // the dashboard. The "special" role only has Estimate visibility, so
      // we drop them straight onto /estimates.
      const role = (result && typeof result === "object") ? result.role : "";
      if (role === "special") {
        navigate("/estimates", { replace: true });
      } else {
        navigate("/");
      }
    } else {
      toast.error(error || "Login failed");
    }
  }

  return (
    <div className="min-h-screen lg:h-screen flex flex-col lg:flex-row bg-[#0B1E3F] overflow-hidden">
      <Toaster richColors position="top-right" />

      {/* ─────────────────────── LEFT · Motivational panel (60%) ─────────────────────── */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden">
        {/* Triptych of themed visuals — electrical · civil · facility */}
        <div className="absolute inset-0 grid grid-cols-3">
          <div className="relative">
            <img src={BG_IMAGES.electrical} alt="" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="relative">
            <img src={BG_IMAGES.civil} alt="" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="relative">
            <img src={BG_IMAGES.facility} alt="" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>

        {/* Dark navy gradient overlay so text reads */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1E3F]/95 via-[#102B5E]/90 to-[#0B1E3F]/95" />
        <div className="absolute inset-0 bg-[#0B1E3F]/30" />

        {/* Foreground content */}
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 text-white w-full">
          {/* Top icon strip */}
          <div className="flex items-center gap-6 text-amber-300">
            <div className="flex items-center gap-2">
              <Zap size={20} /> <span className="text-[11px] uppercase tracking-[0.3em]">Electrical</span>
            </div>
            <div className="w-px h-5 bg-amber-300/40" />
            <div className="flex items-center gap-2">
              <Building2 size={20} /> <span className="text-[11px] uppercase tracking-[0.3em]">Civil</span>
            </div>
            <div className="w-px h-5 bg-amber-300/40" />
            <div className="flex items-center gap-2">
              <Wrench size={20} /> <span className="text-[11px] uppercase tracking-[0.3em]">Facility</span>
            </div>
          </div>

          {/* CEO's Voice + Radiants tagline */}
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.4em] text-amber-300 mb-3 font-black">Your CEO's Voice</div>
            <blockquote
              data-testid="login-ceo-quote"
              className="font-display text-3xl xl:text-4xl font-black tracking-tight leading-[1.18]"
            >
              "Work with Integrity.<br />
              Record with Accuracy.<br />
              Deliver with Excellence."
            </blockquote>

            <div className="mt-10 pl-5 border-l-4 border-[#F97316]" data-testid="login-radiants-tagline">
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-200 mb-2 font-bold">At Radiants</div>
              <p className="font-display text-xl xl:text-2xl font-black tracking-tight leading-snug text-white">
                "Every Entry Matters.<br />
                Every Project Counts.<br />
                Every Team Member Contributes."
              </p>
            </div>
          </div>

          {/* Bottom accent line */}
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-amber-200/80">
            <span>Radiants Civiletric Pvt Ltd</span>
            <span>Cloud ERP · Secure · Reliable</span>
          </div>
        </div>
      </div>

      {/* ─────────────────────── RIGHT · Login card (40%) ─────────────────────── */}
      <div className="w-full lg:w-2/5 flex items-center justify-center p-6 lg:p-10 bg-gradient-to-br from-white via-[#F4F8FE] to-[#E5EDF8] relative overflow-hidden">
        {/* Soft decorative blobs */}
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-amber-200/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#F97316]/20 rounded-full blur-3xl" />

        <div className="relative w-full max-w-md">
          {/* Larger company logo, centred */}
          <div className="flex flex-col items-center mb-8" data-testid="login-brand">
            {brand?.logo_url ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`}
                alt="Radiants Logo"
                className="w-56 h-28 object-contain mb-4"
                data-testid="login-logo"
              />
            ) : (
              <div className="w-56 h-28 flex items-center justify-center font-black text-[#0B1E3F] text-3xl tracking-wide mb-4">RADIANTS</div>
            )}
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#F97316] font-black">PrimeWork</div>
            <div className="text-[12px] italic text-slate-500 mt-1 font-serif">"Radiants is my Pride"</div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {idleNotice && (
              <div data-testid="idle-logout-notice" className="text-sm text-amber-800 border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2">
                <Lock size={14} className="mt-0.5 shrink-0" />
                <span>You were signed out after 30 minutes of inactivity. Please sign in again.</span>
              </div>
            )}
            {pwChangedNotice && (
              <div data-testid="pw-changed-notice" className="text-sm text-amber-800 border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2">
                <Lock size={14} className="mt-0.5 shrink-0" />
                <span>Your password was changed. Please sign in with your new password.</span>
              </div>
            )}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 mb-2">Email</label>
              <div className="relative">
                <User className="absolute left-3 top-3.5 text-slate-400" size={16} />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  placeholder="you@radiants.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border-2 border-slate-200 bg-white pl-10 pr-4 py-3 focus:outline-none focus:border-[#F97316] transition-colors text-slate-800 shadow-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 text-slate-400" size={16} />
                <input
                  data-testid="login-password-input"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border-2 border-slate-200 bg-white pl-10 pr-4 py-3 focus:outline-none focus:border-[#F97316] transition-colors text-slate-800 shadow-sm"
                />
              </div>
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#F97316] to-[#FF8533] hover:from-[#EA580C] hover:to-[#F97316] text-white font-black uppercase tracking-[0.18em] py-3.5 transition-all shadow-lg disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Signing In…" : "Sign In →"}
            </button>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-700 border border-red-200 bg-red-50 px-3 py-2">
                {error}
              </div>
            )}
          </form>

          <div className="mt-10 text-center text-[10px] uppercase tracking-[0.3em] text-slate-400">
            © {new Date().getFullYear()} Radiants Civiletric Pvt Ltd
          </div>
        </div>
      </div>

      {/* Mobile brand strip (visible only on small screens, replaces left panel) */}
      <div className="lg:hidden absolute top-0 inset-x-0 bg-gradient-to-r from-[#0B1E3F] via-[#102B5E] to-[#0B1E3F] text-white px-6 py-3 flex items-center justify-between z-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-black">Radiants PrimeWork</div>
        <div className="text-[10px] italic text-amber-200">"Every Entry Matters."</div>
      </div>
    </div>
  );
}
